import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRollbar } from '@rollbar/react'

import Title from './Title'
import HeaderStatusInfo from './HeaderStatusInfo'
import Loading from './Loading'
import CancelButton from './CancelButton'

import useGncHistory from '../../../hooks/useGncHistory'
import useSharePointToken from '../../../hooks/useSharePointToken'
import useAuthentication from '../../../hooks/useAuthentication'

/**
 * Shared SharePoint upload component for Documents and Records
 * 
 * @param {Object} props
 * @param {string} props.entityType - 'Document' or 'Record'
 * @param {string} props.apiPath - API path segment, e.g. 'documents' or 'records'
 * @param {string} props.statusField - Field name for status in API response, e.g. 'document_status' or 'record_status'
 */
const SharePointUpload = ({ entityType, apiPath, statusField }) => {
    const params = useParams();
    const isNew = params.objID === 'new';
    const objID = Number(params.objID);
    const projectID = params.projectID;
    const navigate = useNavigate();
    const rollbar = useRollbar();
    const { currentUser } = useAuthentication();
  
    const { encryptionKey, tokenIsValid, redirectToAuthUrl } = useSharePointToken()
    const {
        goBack,
    } = useGncHistory()

    const [id, setID] = useState(objID)
    const [reference, setReference] = useState('')
    const [title, setTitle] = useState('')
    const [statusName, setStatusName] = useState('')
    const [sharePointFolder, setSharePointFolder] = useState(null)
  
    const [hasLoaded, setHasLoaded] = useState(false)
    const [hasSaved, setHasSaved] = useState(false)

    const [ isUploading, setIsUploading ] = useState(false)
    const [ uploadResult, setUploadResult ] = useState(null)
    const [ uploadError, setUploadError ] = useState(null)
    const [ authenticateSpinner, setAuthenticateSpinner ] = useState(false)
    
    useEffect(() => {
        if (isNew) {
            return
        }
        fetchObject()
    }, [])
    
    const fetchObject = () => {
      // Separate call to get the Sharepoint folder :-/
      fetch(`/v0/${apiPath}/${objID}/sharepoint_folder`, { credentials: 'same-origin' })
        .then(function(response) {
          if (response.ok) {
            return response.json()
          } else {
            throw new Error(response.statusText)
          }
        })
        .then(function(json) {
          if(json.error) {
            throw new Error(json.error)
          }

          setSharePointFolder(json.result)
          
          fetch(`/v0/${apiPath}/${objID}`, { credentials: 'same-origin' })
                 .then(function(response) {
                   if (response.ok) {
                     return response.json()
                   } else {
                     throw new Error(response.statusText)
                   }
                 })
                 .then(function(json) {
                   if(json.error) {
                     throw new Error(json.error)
                   }

                   const obj = json.result
                   setReference(obj.reference)
                   setTitle(obj.title)
                   setStatusName(obj[statusField]?.name)
                   setHasLoaded(true)
                 })
                 .catch(err => {
                   console.log(`${entityType} fetch error`, err)
                 })

        })
        .catch(err => {
          console.log(`${entityType} fetch sharepoint folder error`, err)
        })
      
    }

    const handleAuthenticate = () => {
        setAuthenticateSpinner(true);
        redirectToAuthUrl()
    }

    const handleUpload = async () => {
        if (isUploading) {
          return
        }
      
        setIsUploading(true)
        setUploadResult(null)
        setUploadError(null)
        
        try {
            const response = await fetch(
                `/v0/${apiPath}/${objID}/upload_to_sharepoint?encryption_key=${encryptionKey}`,
                {
                    credentials: 'same-origin',
                    method: 'POST'
                }
            )
                
            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.error || 'Upload failed')
            }
            
            const json = await response.json()
            console.log("Set upload result", json.result)
            setUploadResult(json.result)
            setHasSaved(true)
        } catch (error) {
            rollbar?.error?.("Error uploading document to SharePoint, error " + error.message, error, {
              currentUserId: currentUser?.id,
              objectType: apiPath,
              objectId: objID,
            })
            setUploadError(error.message)
        } finally {
            setIsUploading(false)
        }
    }

    const handleOpenFile = () => {
        const obj = uploadResult
        const path = obj.file_paths?.[0]
        window.open(path, "_blank", "noopener,noreferrer")
    }
    
    const pageContent = useMemo(() => {
        if (!hasLoaded) {
            return <Loading inline={true}/>
        }

        if (!tokenIsValid) {
            return (
                <>
                <p>
                    To save the {entityType.toLowerCase()} to SharePoint, you must first authenticate.
                </p>
                <CancelButton navigateOverride={() => navigate(`/portfolio/${projectID}/${apiPath}/${objID}`)} />
                <button 
                    className={`btn btn-mr has-spinner btn-primary${authenticateSpinner ? ' active' : ''}`}
                    role="button"
                    onClick={() => handleAuthenticate() }
                >
                    <span className="psc-spinner"><i className="fa fa-refresh fa-spin fa-fw"></i></span>
                    Authenticate via SharePoint
                </button>
                </>
            )
        }
        
        if (hasSaved) {
            return (
                <>
                <p>
                    The {entityType.toLowerCase()} has been saved in SharePoint.
                </p>
                <CancelButton label="OK" navigateOverride={() => navigate(`/portfolio/${projectID}/${apiPath}`)} />
                <button 
                    className="btn btn-mr btn-primary" 
                    onClick={handleOpenFile}
                >
                    Open
                </button>
                </>
            )
            
        }
        
        return (
            <>
            <p>
                To save the {entityType.toLowerCase()} to SharePoint, click the button below.
            </p>
            <p>
              Based on its Applicability, this document will be saved to your SharePoint location:
              <strong> {sharePointFolder?.site_name} | {sharePointFolder?.file_path} </strong>
            </p>
            
            <CancelButton navigateOverride={() => navigate(`/portfolio/${projectID}/${apiPath}/${objID}`)} />
            <button 
                className="btn btn-mr btn-primary" 
                onClick={handleUpload}
            >
                {isUploading ?
                 <><i className="fa fa-refresh fa-spin fa-fw"></i> Uploading...</>
                 : 
                     <><i className="fa fa-cloud-upload"></i> Upload to SharePoint</>
                }
            </button>
            
            {uploadResult &&
             <span className="text-success" style={{marginLeft: '10px'}}>
                 <i className="fa fa-check"></i> Uploaded successfully
             </span>
            }
            
            {uploadError && (
                 <span className="text-danger" style={{marginLeft: '10px'}}>
                     <i className="fa fa-exclamation-triangle"></i> {uploadError}
                 </span>
             )}
            </>
        )
    }, [hasLoaded, tokenIsValid, hasSaved, isUploading, uploadResult, uploadError, entityType, authenticateSpinner])
    
    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            
                            <HeaderStatusInfo
                                type={entityType}
                                id={id}
                                status={statusName}
                            />

                            <Title
                                type={entityType}
                                reference={reference}
                                title={title}
                            />
                        </div>
                        <div className="ibox-content">
                            {pageContent}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default SharePointUpload
